import { create } from 'zustand';
import { Howl } from 'howler';
import { tracksApi } from '../api/tracks';
import type { Track } from '../types';
import { useRadioStore } from './radioStore';

// Set up Media Session for lock screen controls
function setupMediaSession(track: Track, get: () => PlayerState) {
  if (!('mediaSession' in navigator)) return;

  const artworkSrc = track.image || '/landing/app-icon-512.png';
  const artworkType = track.image ? 'image/jpeg' : 'image/png';

  navigator.mediaSession.metadata = new MediaMetadata({
    title: track.name || 'Fonorex',
    artist: 'Fonorex',
    artwork: [
      { src: artworkSrc, sizes: '96x96', type: artworkType },
      { src: artworkSrc, sizes: '192x192', type: artworkType },
      { src: artworkSrc, sizes: '512x512', type: artworkType },
    ],
  });

  navigator.mediaSession.setActionHandler('play', () => get().resume());
  navigator.mediaSession.setActionHandler('pause', () => get().pause());
  navigator.mediaSession.setActionHandler('previoustrack', () => get().previous());
  navigator.mediaSession.setActionHandler('nexttrack', () => get().next());
}

interface PlayerState {
  currentTrack: Track | null;
  isPlaying: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  queue: Track[];
  history: Track[];
  howl: Howl | null;

  // Actions
  playTrack: (track: Track) => void;
  playTrackWithQueue: (track: Track, queue: Track[]) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  next: () => void;
  previous: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  updateTime: (time: number) => void;
}

export const usePlayerStore = create<PlayerState>((set, get) => ({
  currentTrack: null,
  isPlaying: false,
  volume: 0.8,
  currentTime: 0,
  duration: 0,
  queue: [],
  history: [],
  howl: null,

  playTrack: (track: Track) => {
    const state = get();

    // Stop radio if it's playing
    useRadioStore.getState().stopRadio();

    // Add current track to history before switching
    if (state.currentTrack) {
      set({ history: [...state.history, state.currentTrack] });
    }

    // Stop current track if playing
    if (state.howl) {
      state.howl.unload();
    }

    // If track has no source, we can't play it
    if (!track.src) {
      console.error('Track has no source URL');
      return;
    }

    // Create new Howl instance
    const howl = new Howl({
      src: [track.src],
      html5: true,
      volume: state.volume,
      onplay: () => {
        set({ isPlaying: true });

        // Log play to analytics
        tracksApi.logPlay(track._id).catch(err => {
          console.error('Failed to log play:', err);
        });

        // Update time every second
        const updateTime = () => {
          if (get().isPlaying) {
            const currentTime = howl.seek() as number;
            set({ currentTime });
            requestAnimationFrame(updateTime);
          }
        };
        requestAnimationFrame(updateTime);
      },
      onpause: () => set({ isPlaying: false }),
      onstop: () => set({ isPlaying: false, currentTime: 0 }),
      onend: () => {
        // Auto-play next track in queue
        const { queue, next } = get();
        if (queue.length > 0) {
          next();
        } else {
          set({ isPlaying: false, currentTime: 0 });
        }
      },
      onload: () => {
        set({ duration: howl.duration() });
      },
      onloaderror: (_id, error) => {
        console.error('Error loading audio:', error);
      },
      onplayerror: (_id, error) => {
        console.error('Error playing audio:', error);
      },
    });

    howl.play();

    // Set up Media Session for lock screen controls
    setupMediaSession(track, get);

    set({
      currentTrack: track,
      howl,
      currentTime: 0,
      duration: track.duration,
    });
  },

  playTrackWithQueue: (track: Track, queue: Track[]) => {
    // Set the queue first
    set({ queue });
    // Then play the track using existing playTrack logic
    get().playTrack(track);
  },

  pause: () => {
    const { howl } = get();
    if (howl) {
      howl.pause();
      set({ isPlaying: false });
    }
  },

  resume: () => {
    const { howl } = get();
    if (howl) {
      howl.play();
      set({ isPlaying: true });
    }
  },

  stop: () => {
    const { howl } = get();
    if (howl) {
      // unload (not just stop) so the underlying HTMLAudioElement is
      // released — otherwise it can hold the audio session and the
      // radio's AudioContext destination stays silent on iOS/Safari.
      howl.unload();
    }
    set({
      isPlaying: false,
      currentTime: 0,
      currentTrack: null,
      howl: null,
    });
    // Clear media session
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }
  },

  next: () => {
    const { queue } = get();
    if (queue.length > 0) {
      const [nextTrack, ...remainingQueue] = queue;
      set({ queue: remainingQueue });
      get().playTrack(nextTrack);
    }
  },

  previous: () => {
    const { currentTime, currentTrack, history, queue } = get();

    // If more than 3 seconds played, restart current track
    if (currentTime > 3) {
      get().seek(0);
      return;
    }

    // If we have history, play the previous track
    if (history.length > 0) {
      const previousTrack = history[history.length - 1];

      // Check if previous track has a valid source
      if (!previousTrack.src) {
        console.error('Previous track has no source URL');
        return;
      }

      const newHistory = history.slice(0, -1);

      // Update history (remove last item)
      set({ history: newHistory });

      // Add current track to the beginning of the queue
      if (currentTrack) {
        set({ queue: [currentTrack, ...queue] });
      }

      // Play the previous track (but don't add to history again)
      const state = get();
      if (state.howl) {
        state.howl.unload();
      }

      const howl = new Howl({
        src: [previousTrack.src],
        html5: true,
        volume: state.volume,
        onplay: () => {
          set({ isPlaying: true });
          const updateTime = () => {
            if (get().isPlaying) {
              const currentTime = howl.seek() as number;
              set({ currentTime });
              requestAnimationFrame(updateTime);
            }
          };
          requestAnimationFrame(updateTime);
        },
        onpause: () => set({ isPlaying: false }),
        onstop: () => set({ isPlaying: false, currentTime: 0 }),
        onend: () => {
          const { queue, next } = get();
          if (queue.length > 0) {
            next();
          } else {
            set({ isPlaying: false, currentTime: 0 });
          }
        },
        onload: () => {
          set({ duration: howl.duration() });
        },
        onloaderror: (_id, error) => {
          console.error('Error loading audio:', error);
        },
        onplayerror: (_id, error) => {
          console.error('Error playing audio:', error);
        },
      });

      howl.play();

      // Set up Media Session for lock screen controls
      setupMediaSession(previousTrack, get);

      set({
        currentTrack: previousTrack,
        howl,
        currentTime: 0,
        duration: previousTrack.duration,
      });
    }
  },

  seek: (time: number) => {
    const { howl } = get();
    if (howl) {
      howl.seek(time);
      set({ currentTime: time });
    }
  },

  setVolume: (volume: number) => {
    const { howl } = get();
    const clampedVolume = Math.max(0, Math.min(1, volume));

    if (howl) {
      howl.volume(clampedVolume);
    }

    set({ volume: clampedVolume });
  },

  addToQueue: (track: Track) => {
    set((state) => ({
      queue: [...state.queue, track],
    }));
  },

  removeFromQueue: (index: number) => {
    set((state) => ({
      queue: state.queue.filter((_, i) => i !== index),
    }));
  },

  clearQueue: () => {
    set({ queue: [], history: [] });
  },

  updateTime: (time: number) => {
    set({ currentTime: time });
  },
}));
